import {FullGameResponseApiModel} from "../models/full-game-response-api-model";
import {FullGameDataSource} from "../models/full-game-data-source";
import {BaseFactory} from "../../../infrastructure/factories/base-factory";
import {CompetitionApiModel} from "../../common/competition/models/competition-api-model";
import {FullGameApiModel} from "../models/full-game-api-model";
import {FullGameDomainModel} from "../models/full-game-domain-model";
import {CountryApiModel} from "../../common/country/models/country-api-model";
import {BookmakerApiModel} from "../../common/bookmaker/models/bookmaker-api-model";
import {PartialGameDomainModel} from "../../partial-game/models/partial-game-domain-model";
import {PartialGameFactory} from "../../partial-game/services/partial-game.factory";
import {BookmakerDomainModel} from "../../common/bookmaker/models/bookmaker-domain-model";
import {TvNetworkDomainModel} from "../../common/tv-network/models/tv-network-domain-model";
import {OfficialDomainModel} from "../models/official/official-domain-model";
import {OddsDomainModel} from "../../common/odds/models/odds.domain-model";
import {EventDomainModel} from "../../common/events/models/event-domain-model";
import {EventApiModel} from "../../common/events/models/event-api-model";
import {StageDomainModel} from "../models/stage/stage-domain-model";
import {StageApiModel} from "../models/stage/stage-api-model";
import {LineupsMemberDomainModel} from "../../yard/lineups/lineups-member/lineups-member-domain-model";
import {EventViewModel} from "../../common/events/models/event-view-model";
import {SOCCER_EVENT_TYPES} from "../../../core/app/consts";
import {CountryDomainModel} from "../../common/country/models/country-domain-model";
import {CountryFactory} from "../../common/country/domain-services/country-factory";
import {BookmakerFactory} from "../../common/bookmaker/domain-services/bookmaker-factory";
import {OfficialApiModel} from "../models/official/official-api-model";
import {TvNetworkFactory} from "../../common/tv-network/domain-services/tv-network.factory";
import {OddsFactory} from "../../common/odds/domain-services/odds.factory";
import {EventFactory} from "../../common/events/domain-service/event-factory";
import {OddsApiModel} from "../../common/odds/models/odds.api-model";
import {RateOptionApiModel} from "../../common/odds/rate-option/models/rate-option.api-model";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {GameWidgetApiModel} from "../models/widgets/game-widget.api-model";
import {GameWidgetDomainModel} from "../models/widgets/game-widget.domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {TopPerformersDomainModel} from "../models/top-performers/top-performers.domain-model";
import {LiveStreamingDomainModel} from "../models/live-streaming/live-streaming-domain-model";
import {LiveStreamingApiModel} from "../models/live-streaming/live-streaming-api-model";
import {TvNetworkApiModel} from "../../common/tv-network/models/tv-network-api-model";
import {SportsFactory} from "../../sports/domain-services/sports.factory";
import {CompetitionFactory} from "../../common/competition/domain-services/competition-factory";
import type {MemberApiModel} from "../../yard/member/member-api-model";
import {MemberDomainModel} from "../../yard/member/member-domain-model";
import {ChartEventsDomainModel} from "../models/chart-events/chart-events.domain-model";
import {MemberFactory} from "../../yard/member/member-factory";
import {LiveOddsDomainModel} from "../models/live-odds/live-odds.domain-model";
import type {LiveOddsApiModel} from "../models/live-odds/live-odds.api-model";

export class FullGameResponseFactory extends BaseFactory {
  oddsFactory: OddsFactory;
  eventFactory: EventFactory;
  countryFactory: CountryFactory;
  bookmakerFactory: BookmakerFactory;
  tvNetworkFactory: TvNetworkFactory;
  partialGameFactory: PartialGameFactory;
  sportFactory: SportsFactory;

  static injectables = [...BaseFactory.injectables, PartialGameFactory, OddsFactory, EventFactory, CountryFactory, BookmakerFactory, TvNetworkFactory, SportsFactory, CompetitionFactory, MemberFactory];

  constructor(logger: LoggerUtils, terms: TermsUtils, partialGameFactory: PartialGameFactory, oddsFactory: OddsFactory, eventFactory: EventFactory, countryFactory: CountryFactory, bookmakerFactory: BookmakerFactory, tvNetworkFactory: TvNetworkFactory, sportFactory: SportsFactory, competitionFactory: CompetitionFactory, memberFactory: MemberFactory) {
    super(logger, terms);

    contractsUtils.requiredType(partialGameFactory, PartialGameFactory);
    contractsUtils.requiredType(oddsFactory, OddsFactory);
    contractsUtils.requiredType(eventFactory, EventFactory);
    contractsUtils.requiredType(countryFactory, CountryFactory);
    contractsUtils.requiredType(bookmakerFactory, BookmakerFactory);
    contractsUtils.requiredType(tvNetworkFactory, TvNetworkFactory);
    contractsUtils.requiredType(sportFactory, SportsFactory);
    contractsUtils.requiredType(competitionFactory, CompetitionFactory);
    contractsUtils.requiredType(memberFactory, MemberFactory);

    this.partialGameFactory = partialGameFactory;
    this.oddsFactory = oddsFactory;
    this.eventFactory = eventFactory;
    this.countryFactory = countryFactory;
    this.bookmakerFactory = bookmakerFactory;
    this.tvNetworkFactory = tvNetworkFactory;
    this.sportFactory = sportFactory;
    this.competitionFactory = competitionFactory;
    this.memberFactory = memberFactory;
  }

  createDomainModel(apiModel: FullGameResponseApiModel): FullGameDataSource {
    const countriesMap: Map<Number, CountryDomainModel> = this.countryFactory.createDomainMap(apiModel.countries, CountryDomainModel);
    const competitionsMap: Map<Number, CompetitionDomainModel> = this.competitionFactory.createDomainMap(apiModel.competitions, CompetitionDomainModel, 'id', apiModel.sports, apiModel.countries);

    const game: FullGameApiModel = apiModel.game || {};
    const competition: CompetitionApiModel = competitionsMap.get(game.competitionId);
    const bookmakers: BookmakerDomainModel = apiModel.bookmakers?.map((bookmaker: BookmakerApiModel) => new BookmakerDomainModel(bookmaker));
    const model: FullGameDomainModel = this.createFullGameDomainObject(apiModel, game, competition, countriesMap, apiModel.bookmakers);
    const response: FullGameDataSource = new FullGameDataSource(model, apiModel, bookmakers);
    return response;
  }

  createFullGameDomainObject(responseApiModel: FullGameResponseApiModel, apiModel: FullGameApiModel, competitionDomainModel: CompetitionDomainModel, countriesMap: Map<Number, CountryDomainModel>, bookmakersApiModel: Array<BookmakerApiModel>): FullGameDomainModel {
    const partialGame: PartialGameDomainModel = this.partialGameFactory.createPartialGameDomainObject(apiModel, competitionDomainModel, countriesMap, competitionDomainModel?.sport);
    const bookmakers: BookmakerDomainModel = bookmakersApiModel?.map((bookmaker: BookmakerApiModel) => new BookmakerDomainModel(bookmaker));
    const tvNetworks: Array<TvNetworkDomainModel> = (apiModel?.tvNetworks || []).map((tv: TvNetworkApiModel) => this.tvNetworkFactory.createDomainModel(tv, bookmakersApiModel, countriesMap));
    const officials: Array<OfficialDomainModel> = apiModel.officials?.map((official: OfficialApiModel) => {
      const officialCountry: CountryApiModel = countriesMap.get(official.countryId);

      return this.createOfficialDomainObject(official, officialCountry)
    });
    const currentOdds: OddsDomainModel = apiModel.bestOdds?.map((odds: OddsApiModel) => this.oddsFactory.createDomainModel(odds, bookmakers));
    const isNational: Boolean = partialGame.homeCompetitor.isNational && partialGame.awayCompetitor.isNational;
    const members: Array<MemberDomainModel> = apiModel.members?.map((member: MemberApiModel) => new MemberDomainModel(member, isNational, competitionDomainModel.sport));
    const events: Array<EventDomainModel> = apiModel.events?.map((event: EventApiModel) => this.eventFactory.createDomainModel(event, members, partialGame.homeCompetitor, partialGame.awayCompetitor, competitionDomainModel.sport));
    const widgets: Array<GameWidgetDomainModel> = apiModel.widgets?.map((widget: GameWidgetApiModel) => new GameWidgetDomainModel(widget));
    const relatedGames: Array<PartialGameDomainModel> = apiModel.relatedGames?.map(g => this.partialGameFactory.createPartialGameDomainObject(g, competitionDomainModel, countriesMap, competitionDomainModel.sport));
    const topPerformers: TopPerformersDomainModel = apiModel.topPerformers ? new TopPerformersDomainModel(apiModel.topPerformers, partialGame.homeCompetitor.isNational, competitionDomainModel.sport) : null;
    const liveStreaming: Array<LiveStreamingDomainModel> = apiModel.liveStreaming?.map((liveStreaming: LiveStreamingApiModel) => this.createLiveStreamingDomainObject(liveStreaming, tvNetworks));
    const chartEvents: ChartEventsDomainModel = apiModel.chartEvents ? new ChartEventsDomainModel(apiModel.chartEvents, this.memberFactory.createMap(members)) : null;
    const liveOdds: LiveOddsDomainModel = apiModel.liveOdds ? new LiveOddsDomainModel(apiModel.liveOdds, bookmakers) : null;

    const game: FullGameDomainModel = new FullGameDomainModel(apiModel, partialGame, officials, tvNetworks, currentOdds, events, members, widgets, relatedGames, topPerformers, liveStreaming, chartEvents, liveOdds);

    this._calcEventsForAllMembers(game);

    return game;
  }

  _calcEventsForAllMembers(game: FullGameDomainModel) {
    if (game.homeCompetitor.lineups) {
      game.homeCompetitor.lineups.members.forEach((member: LineupsMemberDomainModel) => {
        const events: Array<EventViewModel> = this._extractEventsForMember(member.id, game.events) || [];

        member.events.push(...events);
      })
    }

    if (game.awayCompetitor.lineups) {
      game.awayCompetitor.lineups.members.forEach((member: LineupsMemberDomainModel) => {
        const events: Array<EventViewModel> = this._extractEventsForMember(member.id, game.events) || [];

        member.events.push(...events);
      })
    }
  }

  _extractEventsForMember(playerId: LineupsMemberDomainModel, events: Array<EventDomainModel> = []) {
    const memberEvents: Array<EventViewModel> = events.filter(x => !!x).filter((event: EventViewModel) => {
      if (event.eventType.id === SOCCER_EVENT_TYPES.substitution) {
        return event.player.id === playerId || event.extraPlayers[0].id === playerId;
      }

      return event.player.id === playerId;
    });

    return memberEvents;
  }

  createOfficialDomainObject(apiModel: OfficialApiModel, country: CountryDomainModel): OfficialDomainModel {
    return new OfficialDomainModel(apiModel, country);
  }

  createStageDomainObject(apiModel: StageApiModel): StageDomainModel {
    const domain: StageDomainModel = new StageDomainModel(apiModel);
    return domain;
  }

  createLiveStreamingDomainObject(apiModel: LiveStreamingApiModel, tvNetworks: TvNetworkDomainModel): LiveStreamingDomainModel {
    const domain: LiveStreamingDomainModel = new LiveStreamingDomainModel(apiModel, tvNetworks);
    return domain;
  }

  updateApiResponseModel(response: FullGameResponseApiModel, delta: FullGameResponseApiModel) {
    if (!response || !delta) {
      return;
    }

    this.updateModel(response, delta);
    this.updateApiModelArray(response.competitions, delta.competitions);
    this.updateApiModelArray(response.countries, delta.countries);
    this.updateApiModelArray(response.sports, delta.sports);
    this.updateApiModelArray(response.bookmakers, delta.bookmakers);
    this.updateApiModelArray(response.bookmakers, delta.bookmakers);
    this.updateFullGameApiModel(response.game, delta.game);

    return response;
  }

  updateFullGameApiModel(response: FullGameApiModel, delta: FullGameApiModel) {
    if (response) {
      response.animation = delta?.animation;
    }

    if (!response || !delta) {
      response?.events?.forEach((event: EventApiModel) => event.isNew = false);

      return;
    }

    this.updateModel(response, delta);

    this.partialGameFactory.updateGameCompetitorApiModel(response.homeCompetitor, delta.homeCompetitor);
    this.partialGameFactory.updateGameCompetitorApiModel(response.awayCompetitor, delta.awayCompetitor);

    this.updateModel(response.venue, delta.venue);
    this.updateApiModelArray(response.tvNetworks, delta.tvNetworks);
    this.updateApiModelArray(response.officials, delta.officials);
    this.updateApiModelArray(response.bestOdds, delta.bestOdds, (a, b) => this.updateBestOddsApiModel(a, b), (a, b) => a.lineId === b.lineId);
    this.updateApiModelArray(response.members, delta.members);
    this.updateLiveOddsApiModel(response.liveOdds, delta.liveOdds);
    response.stages = delta.stages;
    response.predictions = delta.predictions;
    response.events = delta.events;
    response.matchFacts = delta.matchFacts;
    
    if(delta.stories) {
      response.stories = delta.stories;
    }
    response.preciseGameTime = delta.preciseGameTime;
    response.addedTime = delta.addedTime;
    response.eventChartPenaltiesDetails = delta.eventChartPenaltiesDetails;
    response.currentPointByPointGame = delta.currentPointByPointGame;
  }

  updateLiveOddsApiModel(response: LiveOddsApiModel, delta: LiveOddsApiModel) {
    if (!response || !delta) {
      return;
    }

    response.lines = delta.lines
  }

  updateBestOddsApiModel(response: OddsApiModel, delta: OddsApiModel) {
    if (!response || !delta) {
      return;
    }
    this.updateModel(response, delta);

    this.updateApiModelArray(response.options, delta.options, (a, b) => this.updateRateOptionsApiModel(a, b), (a, b) => a.num === b.num);
  }

  updateRateOptionsApiModel(response: RateOptionApiModel, delta: RateOptionApiModel) {
    if (!response || !delta) {
      return;
    }
    this.updateModel(response, delta);

    this.updateModel(response.oldRate, delta.oldRate);
    this.updateModel(response.rate, delta.rate);
  }
}
